// ============================================================
// Coloration syntaxique légère (HTML / CSS / JSON) pour l'éditeur
// de thème. Aucune dépendance externe : un tokenizer simple
// renvoie du HTML coloré, à afficher dans un <pre>.
// ============================================================

export type CodeLang = 'html' | 'css' | 'json';

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const span = (cls: string, text: string): string =>
  `<span class="${cls}">${escapeHtml(text)}</span>`;

/** Colore un fragment de balise (`<div class="x">`) une fois connu. */
function highlightTag(tag: string): string {
  // tag = "<div class=\"a\" href='/x'>" ou "</div>"
  const m = tag.match(/^(<\/?)([a-zA-Z0-9:-]+)([\s\S]*?)(\/?>)$/);
  if (!m) return span('tok-tag', tag);
  const [, open, name, attrs, close] = m;
  const attrHtml = attrs.replace(
    /([a-zA-Z0-9:_-]+)(\s*=\s*)("[^"]*"|'[^']*'|[^\s>]+)?/g,
    (_all, attrName: string, eq: string, val?: string) =>
      `${span('tok-attr', attrName)}${escapeHtml(eq)}${val ? span('tok-string', val) : ''}`,
  );
  return `${span('tok-punct', open)}${span('tok-tag', name)}${attrHtml}${span('tok-punct', close)}`;
}

function highlightHtml(code: string): string {
  let out = '';
  let i = 0;
  const n = code.length;
  while (i < n) {
    if (code.startsWith('<!--', i)) {
      const end = code.indexOf('-->', i + 4);
      const stop = end === -1 ? n : end + 3;
      out += span('tok-comment', code.slice(i, stop));
      i = stop;
      continue;
    }
    if (code.startsWith('{{', i)) {
      const end = code.indexOf('}}', i + 2);
      const stop = end === -1 ? n : end + 2;
      out += span('tok-tagzifek', code.slice(i, stop));
      i = stop;
      continue;
    }
    if (code[i] === '<' && /[a-zA-Z/!]/.test(code[i + 1] || '')) {
      const end = code.indexOf('>', i);
      const stop = end === -1 ? n : end + 1;
      out += highlightTag(code.slice(i, stop));
      i = stop;
      continue;
    }
    // texte
    let j = i;
    while (j < n && code[j] !== '<' && !code.startsWith('{{', j)) j += 1;
    out += escapeHtml(code.slice(i, j));
    i = j;
  }
  return out;
}

function highlightCss(code: string): string {
  let out = '';
  let i = 0;
  const n = code.length;
  while (i < n) {
    if (code.startsWith('/*', i)) {
      const end = code.indexOf('*/', i + 2);
      const stop = end === -1 ? n : end + 2;
      out += span('tok-comment', code.slice(i, stop));
      i = stop;
      continue;
    }
    if (code[i] === '@') {
      let j = i;
      while (j < n && code[j] !== ';' && code[j] !== '{' && code[j] !== '\n') j += 1;
      out += span('tok-atrule', code.slice(i, j));
      i = j;
      continue;
    }
    if (code[i] === '"' || code[i] === "'") {
      const q = code[i];
      let j = i + 1;
      while (j < n && code[j] !== q && code[j] !== '\n') j += 1;
      const stop = j < n && code[j] === q ? j + 1 : j;
      out += span('tok-string', code.slice(i, stop));
      i = stop;
      continue;
    }
    if (code[i] === '{' || code[i] === '}' || code[i] === ';') {
      out += span('tok-punct', code[i]);
      i += 1;
      continue;
    }
    if (/[a-zA-Z0-9#.\[_:&>*%~-]/.test(code[i])) {
      let j = i;
      while (j < n && /[a-zA-Z0-9#.\[_\]:&>*%~"'-]/.test(code[j])) j += 1;
      const token = code.slice(i, j);
      const after = code[j];
      if (after === ':') out += span('tok-attr', token);
      else if (after === '{') out += span('tok-tag', token);
      else out += span('tok-value', token);
      i = j;
      continue;
    }
    out += escapeHtml(code[i]);
    i += 1;
  }
  return out;
}

function highlightJson(code: string): string {
  let out = '';
  let i = 0;
  const n = code.length;
  while (i < n) {
    if (code[i] === '"') {
      let j = i + 1;
      while (j < n && !(code[j] === '"' && code[j - 1] !== '\\')) j += 1;
      const stop = j < n ? j + 1 : j;
      const isKey = /^\s*:/.test(code.slice(stop));
      out += span(isKey ? 'tok-attr' : 'tok-string', code.slice(i, stop));
      i = stop;
      continue;
    }
    if (/[-0-9]/.test(code[i])) {
      let j = i;
      while (j < n && /[-0-9.eE+]/.test(code[j])) j += 1;
      out += span('tok-number', code.slice(i, j));
      i = j;
      continue;
    }
    if (/[a-z]/.test(code[i])) {
      let j = i;
      while (j < n && /[a-zA-Z]/.test(code[j])) j += 1;
      out += span('tok-atrule', code.slice(i, j));
      i = j;
      continue;
    }
    out += escapeHtml(code[i]);
    i += 1;
  }
  return out;
}

/** Colore du code selon le langage demandé. */
export function highlightCode(code: string, lang: CodeLang): string {
  if (lang === 'css') return highlightCss(code);
  if (lang === 'json') return highlightJson(code);
  return highlightHtml(code);
}

/** Devine le langage d'un extrait de code. */
export function detectLang(code: string, fallback: CodeLang = 'html'): CodeLang {
  const t = code.trim();
  if (t.startsWith('{') || t.startsWith('[')) return 'json';
  if (/[.@#]?[a-zA-Z][\w-]*\s*\{/.test(t) && !/<[a-zA-Z]/.test(t)) return 'css';
  return fallback;
}

/**
 * Réindente grossièrement du HTML : chaque balise ouvrante augmente
 * l'indentation, chaque fermante la réduit. Les blocs <pre>/<script>/
 * <style> et le texte interne ne sont pas reformatés.
 */
export function formatHtml(html: string): string {
  const tokens = html
    .replace(/>\s*</g, '><')
    .replace(/></g, '>\n<')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  let depth = 0;
  const out: string[] = [];
  for (const line of tokens) {
    const isClosing = /^<\//.test(line);
    const isSelfClosing = /\/>$/.test(line) || /^<(area|base|br|col|embed|hr|img|input|link|meta|source|track|wbr)\b/i.test(line);
    const isOpening = /^<[a-zA-Z]/.test(line) && !isSelfClosing;
    if (isClosing) depth = Math.max(0, depth - 1);
    out.push('  '.repeat(depth) + line);
    if (isOpening && !/<\/[a-zA-Z]/.test(line)) depth += 1;
  }
  return out.join('\n');
}